/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-extra-boolean-cast */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useFormik } from 'formik';
import {
    Button,
    Divider,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Overflow,
    OverflowItem,
    SelectTabData,
    SelectTabEvent,
    Tab,
    TabList,
    Text,
    Title2,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components';
import { useAccess } from '../../../../auth/useAccess';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { Column, Row, Paths } from '../../../common';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { useToasts } from '../../../toasts/useToasts';
import { CaseDocumentsPage } from '../CaseDocuments/CaseDocumentsPage';
import { CaseEventsPage } from '../CaseEvents/CaseEventsPage';
import { CaseNotesPage } from '../CaseNotes/CaseNotesPage';
import { CaseTasksPage } from '../CaseTasks/CaseTasksPage';
import {
    AppMessage,
    Case,
    CaseEvent,
    CaseEventTemplate,
    CaseTemplate,
    Note,
    Task,
    useInitialValues,
    UserAccount,
} from '../../../../data/types';
import { TimeTrackingPage } from '../CaseTimeTracking/TimeTrackingPage';
import {
    useCaseDocumentService,
    useCaseEventService,
    useCaseExpenseService,
    useCaseNoteService,
    useCaseEngagementService,
    useCaseService,
    useInvoiceService,
    useTaskService,
    useTimeTrackingService,
} from '../../../../data/services';
import { CaseInfoPage } from '../CaseInfo/CaseInfoPage';
import { CaseInvoicesPage } from '../CaseInvoices/CaseInvoicesPage';
import { EngagementPage } from '../CaseEngagementDoc/EngagementPage';
import { useCaseMilestoneService } from '../../../../data/services/useCaseMilestoneService';
import { CaseMilestonesPage } from '../CaseMilestones/CaseMilestonesPage';
import { CaseServicesProvider } from './CaseServicesProvider';
import { useRecentCasesService } from '../../../../data/services/useRecentCasesService';
import {
    DeleteRegular,
    HistoryRegular,
    MoreHorizontalRegular,
    MoreVerticalRegular,
    PuzzleCubePieceRegular,
} from '@fluentui/react-icons';
import { useCaseTemplateService } from '../../../../data/services/useCaseTemplateService';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { AuditHistoryDialog } from '../../../dialogs/AuditHistoryDialog';
import { CaseInsightsPage } from '../CaseInsights/CaseInsights';
import { AppMessageDisplay, useAppMessageDisplay } from '../../../controls/AppMessageDisplay';
import { useValidation } from '../../../../data/types/validation';
import { CaseTabs } from '../../Tabs/CaseTabs';
import { appTokens } from '../../../common/Styles/appTokens';
import { useMediaSizes } from '../../../common/Styles/useMediaSizes';
import { useCaseLevelSignalR } from '../../../../signalR/useCaseLevelSignalR';
import { CaseClientPortalPage } from '../CaseClientPortal/CaseClientPortalPage';

const useStyles = makeStyles({
    tabList: {
        paddingBlockEnd: '.25rem',
        width: '100%',
        minWidth: 0,
        flexWrap: 'nowrap',
        overflow: 'hidden',
    },
    overflowMenuButton: {
        alignSelf: 'center',
    },
    content: {
        borderTop: `2px solid ${tokens.colorBrandBackground}`,
    },
});

type CaseTabOption = {
    value: string;
    label: string;
};
export type ViewEditCaseProps = {
    onSubmit?: (values: Case) => void;
};
export const ViewEditCase = (props: ViewEditCaseProps) => {
    const { onSubmit } = props;

    //Params
    const { caseid } = useParams();

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user, account } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const initValues = useInitialValues();
    const toasts = useToasts();
    const navTo = useNavigate();
    const { state } = useLocation();
    const selectedTab = state?.tab || CaseTabs.caseInfo;
    const messageDisplay = useAppMessageDisplay();
    const { validateCaseObj } = useValidation();
    const mediaSizes = useMediaSizes();

    //Data Services
    const recentCaseService = useRecentCasesService();
    const caseService = useCaseService();

    const caseTemplateService = useCaseTemplateService();
    const caseEngagementService = useCaseEngagementService(caseid as string);
    const eventService = useCaseEventService(caseid as string);
    const documentService = useCaseDocumentService(caseid as string);
    const expenseService = useCaseExpenseService(caseid as string);
    const milestoneService = useCaseMilestoneService(caseid as string);
    const noteService = useCaseNoteService(caseid as string);
    const taskService = useTaskService();
    const timeTrackingService = useTimeTrackingService();
    const invoiceService = useInvoiceService(caseid as string);

    useCaseLevelSignalR(caseid as string);

    //Initializers
    useEffect(() => {
        const initializeServices = async () => {
            await Promise.all([
                caseEngagementService.loadEngagementDocsAsync(),
                eventService.loadCaseEventsAsync(),
                documentService.loadDocumentsAsync(),
                expenseService.loadCaseExpensesAsync(),
                milestoneService.loadMilestonesAsync(),
                noteService.loadNotesAsync(),
                taskService.loadCaseTasksAsync(caseid!),
                timeTrackingService.loadTimeEntriesForCase(caseid!),
                invoiceService.loadInvoices(),
            ]);
        };

        if (caseid) {
            initializeServices();
        }
    }, [caseid]);

    //State
    const [delConfirmOpen, setDelConfirmOpen] = useState<boolean>(false);
    const [applyTemplateConfirmOpen, setApplyTemplateConfirmOpen] = useState<boolean>(false);
    const [templateToApply, setTemplateToApply] = useState<CaseTemplate>();
    const [auditHistoryOpen, setAuditHistoryOpen] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const tabClasses = mergeClasses(commonStyles.button.root);

    //Form
    const hasRequiredFields = (caseValues: Case) => {
        return !!caseValues.caseId && !!caseValues.caseName;
    };
    const caseForm = useFormik<Case>({
        initialValues: {
            ...initValues.caseObj(),
            shortDescription: { fieldName: 'Short Description', fieldValue: '' },
        },
        onSubmit: async (values, { resetForm }) => {
            const caseValues = values as Case;
            if (!hasRequiredFields(caseValues)) {
                messageDisplay.addMessage(getMissingFieldsMsg(caseValues));
            } else {
                try {
                    //check if status has changed and if so add a statuschange entry
                    if (caseValues.status?.status !== caseForm.initialValues.status?.status) {
                        caseValues.statusChanges = [
                            ...(caseValues.statusChanges || []),
                            { caseStatus: caseValues.status!, statusDate: new Date() },
                        ];
                    }

                    //submit new case type
                    caseService.upsertCaseAsync(caseValues);
                    toasts.cases.notifyCaseUpdated(caseValues);
                } catch {
                    toasts.cases.notifyCaseUpdateError(caseValues);
                }

                //then send back to calling element
                !!onSubmit && onSubmit(caseValues);
                resetForm({ values });
            }
        },
        validationSchema: validateCaseObj.schema,
        onReset: () => {
            messageDisplay.removeMessage('missingFields');
        },
    });

    //Access
    const canViewCaseEngagement = () => {
        return account.features?.engagement && access.cases.canViewCaseEngagement(caseid);
    };
    const canViewMilestones = () => {
        return account.features?.milestones && access.cases.canViewCaseEngagement(caseid);
    };
    const canViewTasks = () => {
        return account.features?.tasks;
    };
    const canViewEvents = () => {
        return account.features?.events;
    };
    const canViewNotes = () => {
        return account.features?.notes;
    };
    const canViewDocuments = () => {
        return account.features?.documents && access.cases.canViewDocuments(caseid);
    };
    const canViewTimeTracking = () => {
        return account.features?.timeTracking;
    };
    const canViewInvoices = () => {
        return account.features?.invoices && access.invoices.canViewInvoices(caseid);
    };
    const canViewInsights = () => {
        return account.features?.insights;
    };
    const canViewClientPortal = () => {
        return account.features?.clientPortal;
    };
    const canDeleteCases = () => {
        return access.account.canDeleteCases();
    };
    const canSeeAuditHistory = () => {
        return account.features?.auditHistory && access.account.isAccountAdmin();
    };
    const canApplyTemplate = () => {
        return account.features?.templates && access.cases.canEditCaseAsAdmin(caseid);
    };
    const canAccessMoreOptions = () => {
        return canDeleteCases() || canApplyTemplate() || canSeeAuditHistory();
    };
    const canCreateCaseTemplates = () => {
        return account.features?.templates && access.templates.canCreateEditCaseTemplates();
    };

    //Messages
    const getMissingFieldsMsg = (caseVals: Case): AppMessage => {
        return {
            id: 'missingFields',
            intent: 'error',
            title: 'Missing fields:',
            content: (
                <>
                    {!caseVals.caseName && (
                        <Text>
                            <Text italic>'Case Name'</Text> is a required field.{' '}
                        </Text>
                    )}
                </>
            ),
        };
    };

    //Logic
    const canUpdateOnTab = (tab: string) => {
        return tab === CaseTabs.caseInfo || tab == CaseTabs.caseTimeTracking;
    };
    const applyCaseTemplate = async () => {
        if (!templateToApply) return;

        const ct = templateToApply as CaseTemplate;

        //set fields
        caseForm.setFieldValue('caseType', ct.caseType);
        caseForm.setFieldValue('shortDescription', ct.shortDescription);
        caseForm.setFieldValue('overview', ct.overview);
        caseForm.setFieldValue('allowedStatuses', ct.statuses);
        if (!caseForm.values.status) {
            caseForm.setFieldValue('status', ct.statuses?.[0]);
        }

        //set the engagement template
        if (!!ct.engagementTemplate) {
            const eng = {
                ...initValues.caseEngagement(caseForm.values.caseId!),
                ...ct.engagementTemplate,
            };

            await caseEngagementService.upsertCaseEngagementDocAsync(eng);
        }

        //set note
        if (!!ct.notes?.[0]) {
            const note: Note = {
                ...initValues.caseNote(caseForm.values.caseId!),
                text: ct.notes?.[0] || '',
                lifecycle: {
                    createdOn: new Date(),
                    createdBy: primaryAccount?.user.username,
                },
            };
            noteService.upsertCaseNoteAsync(note);
        }

        //set events
        let events = [] as CaseEvent[] | undefined;
        events = ct.events?.map((evt: CaseEventTemplate) => {
            const eventDate = new Date();
            eventDate.setDate(eventDate.getDate() + (evt.daysFromRetained || 0));

            return {
                ...initValues.caseEvent(caseForm.values.caseId!),
                event: evt.eventName,
                description: evt.description,
                date: eventDate,
                due: evt.due,
            } as CaseEvent;
        });
        if (!!events?.length) {
            await Promise.all(events.map((evt) => eventService.upsertCaseEventAsync(evt)));
        }

        //save milestones
        if (!!ct.milestones?.length && !milestoneService.milestones.length) {
            milestoneService.createMilestonesForCase(ct.milestones!, caseForm.values.caseId!, upsertTasks, upsertEvents);
        }

        caseForm.submitForm();
    };
    const upsertTasks = async (tasks: Task[]) => {
        await Promise.all(tasks.map((t) => taskService.upsertTaskAsync(t)));
    };
    const upsertEvents = async (events: CaseEvent[]) => {
        await Promise.all(events.map((evt) => eventService.upsertCaseEventAsync(evt)));
    };
    const deleteCase = async () => {
        try {
            await caseService.deleteCaseAsync(caseid as string);

            toasts.cases.notifyCaseDeleted(caseForm.values);
        } catch {
            toasts.cases.notifyCaseDeleteError(caseForm.values);
        }

        navTo(Paths.cases);
    };

    //UI Events
    const onTabChange = (tab: string) => {
        navTo(Paths.viewEditCase(caseid as string), { state: { tab } });
    };
    const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
        onTabChange(data.value as string);

        //setSelectedTab(data.value as string);
    };
    const clickApplyTemplate = (ct: CaseTemplate, e?: React.MouseEvent) => {
        e?.stopPropagation?.();

        setTemplateToApply(ct);

        //check that the template can be applied

        setApplyTemplateConfirmOpen(true);
    };
    const onClickUpdateCase = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        caseForm.submitForm();
    };
    const onCreateTemplateMenuItemClick = (e: any) => {
        e?.stopPropagation?.();
        navTo(Paths.templates);
    };
    const onDeleteCaseMenuItemClick = (e: any) => {
        e?.stopPropagation?.();
        setDelConfirmOpen(true);
    };
    const onDeleteCaseConfirm = () => {
        deleteCase();
    };
    const onDeleteCaseConfirmOpenChange = (...args: any[]) => {
        const maybeOpen = typeof args[0] === 'boolean' ? args[0] : (args?.[1]?.open ?? args?.[0]);
        if (typeof maybeOpen === 'boolean') {
            setDelConfirmOpen(maybeOpen);
        }
    };
    const onApplyTemplateConfirm = () => {
        applyCaseTemplate();
    };
    const onApplyTemplateConfirmOpenChange = (...args: any[]) => {
        const maybeOpen = typeof args[0] === 'boolean' ? args[0] : (args?.[1]?.open ?? args?.[0]);
        if (typeof maybeOpen === 'boolean') {
            setApplyTemplateConfirmOpen(maybeOpen);
        }
    };

    const caseTabOptions = [
        { value: CaseTabs.caseInfo, label: 'Case Info', access: true },
        { value: CaseTabs.engagement, label: 'Engagement', access: canViewCaseEngagement() },
        { value: CaseTabs.milestones, label: 'Milestones', access: canViewMilestones() },
        { value: CaseTabs.caseTasks, label: 'Tasks', access: canViewTasks() },
        { value: CaseTabs.caseEvents, label: 'Events', access: canViewEvents() },
        { value: CaseTabs.caseNotes, label: 'Notes', access: canViewNotes() },
        { value: CaseTabs.caseDocuments, label: 'Documents', access: canViewDocuments() },
        { value: CaseTabs.caseTimeTracking, label: 'Time Tracking', access: canViewTimeTracking() },
        { value: CaseTabs.invoices, label: 'Invoices', access: canViewInvoices() },
        { value: CaseTabs.insights, label: 'Insights', access: canViewInsights() },
        { value: CaseTabs.clientPortal, label: 'Client Portal', access: canViewClientPortal() },
    ];
    const visibleCaseTabOptions = caseTabOptions.filter((tab) => tab.access);

    const pageContentHeight = mediaSizes.isUnderTablet ? appTokens.pageHeight('9rem') : appTokens.pageHeight('6.5rem');

    //Effects
    useEffect(() => {
        caseService.getCaseAsync(caseid as string).then((theCase: Case) => {
            caseForm.resetForm({
                values: {
                    ...initValues.caseObj(),
                    ...theCase,
                    shortDescription: {
                        ...theCase.shortDescription,
                        fieldValue: theCase.shortDescription?.fieldValue?.substring(
                            0,
                            validateCaseObj.shortDescription.maxLength,
                        ),
                    },
                    overview: theCase.overview?.substring(0, validateCaseObj.overview.maxLength),
                },
            });
            recentCaseService.addRecentCaseAsync(theCase);
        });
    }, [caseid]);

    return (
        <>
            <Column flex='1' gap='medium' maxHeight={appTokens.pageHeight()} overflowY='clip'>
                <Row gap='medium'>
                    <Column width='auto' noGap padding='pr-2'>
                        {!!caseForm.values.caseId && (
                            <>
                                <Text size={400}>{caseForm.values?.retainedBy?.party?.name}</Text>
                                <Title2 className={commonStyles.utility.noSelect}>
                                    {`${caseForm.values?.caseName || ''}`}
                                </Title2>
                                {!!caseForm.values?.status?.status && (
                                    <Column padding='pt-2'>
                                        <Text size={300}>Status: {caseForm.values?.status?.status}</Text>
                                    </Column>
                                )}
                                {/* <Text size={200}>{JSON.stringify(caseForm.errors)}</Text> */}
                            </>
                        )}
                    </Column>
                    <Column vAlign='middle' padding='px-3'>
                        <AppMessageDisplay messages={messageDisplay.messages} onDismiss={messageDisplay.removeMessage} />
                    </Column>
                    <Column width='auto' vAlign='middle' gap='small' align='right'>
                        {access.cases.canEditCaseInfo(caseid) && (
                            <Button
                                appearance='primary'
                                onClick={onClickUpdateCase}
                                disabled={!caseForm.dirty || !caseForm.isValid}
                                data-testid='view-edit-case-update-button'
                            >
                                Update
                            </Button>
                        )}
                    </Column>
                    <Column width='auto' vAlign='middle'>
                        {canAccessMoreOptions() && (
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <Tooltip content='More' relationship='label'>
                                        <Button
                                            icon={<MoreVerticalRegular />}
                                            data-testid='view-edit-case-more-options-button'
                                        />
                                    </Tooltip>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        {canApplyTemplate() && (
                                            <Menu positioning={{ position: 'before' }}>
                                                <MenuTrigger>
                                                    <MenuItem
                                                        icon={<PuzzleCubePieceRegular />}
                                                        data-testid='view-edit-case-apply-template-menu-item'
                                                    >
                                                        Apply a Case Template
                                                    </MenuItem>
                                                </MenuTrigger>
                                                <MenuPopover>
                                                    <MenuList>
                                                        {caseTemplateService.caseTemplates.length ? (
                                                            caseTemplateService.caseTemplates.map((ct: CaseTemplate) => (
                                                                <MenuItem
                                                                    key={ct.templateId}
                                                                    onClick={(e: any) => clickApplyTemplate(ct, e)}
                                                                    data-testid={`view-edit-case-apply-template-menu-item-${ct.templateId}`}
                                                                >
                                                                    {ct.templateName}
                                                                </MenuItem>
                                                            ))
                                                        ) : (
                                                            <>
                                                                {canCreateCaseTemplates() ? (
                                                                    <MenuItem
                                                                        onClick={onCreateTemplateMenuItemClick}
                                                                        data-testid='view-edit-case-create-template-menu-item'
                                                                    >
                                                                        <Column noGap align='center'>
                                                                            <Text size={300}>No templates available.</Text>
                                                                            <Text size={300}>
                                                                                Click here to create case templates.
                                                                            </Text>
                                                                        </Column>
                                                                    </MenuItem>
                                                                ) : (
                                                                    <Text size={300}> No templates available</Text>
                                                                )}
                                                            </>
                                                        )}
                                                    </MenuList>
                                                </MenuPopover>
                                            </Menu>
                                        )}
                                        {canApplyTemplate() && (canDeleteCases() || canSeeAuditHistory()) && (
                                            <Divider style={{ minHeight: '1rem' }} />
                                        )}
                                        {canSeeAuditHistory() && (
                                            <MenuItem
                                                icon={<HistoryRegular />}
                                                onClick={(e: any) => {
                                                    e?.stopPropagation?.();
                                                    setAuditHistoryOpen(true);
                                                }}
                                                data-testid='view-edit-case-audit-history-menu-item'
                                            >
                                                View Audit History
                                            </MenuItem>
                                        )}
                                        {canDeleteCases() && (
                                            <MenuItem
                                                icon={<DeleteRegular />}
                                                onClick={onDeleteCaseMenuItemClick}
                                                data-testid='view-edit-case-delete-menu-item'
                                            >
                                                Delete Case
                                            </MenuItem>
                                        )}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        )}
                    </Column>
                </Row>
                <Column className={styles.content} height={pageContentHeight} gap='small'>
                    <Overflow minimumVisible={1} padding={48}>
                        <TabList
                            className={styles.tabList}
                            size={mediaSizes.isUnderLargeLaptop ? 'small' : mediaSizes.isUnderDesktop ? 'medium' : 'large'}
                            selectedValue={selectedTab}
                            onTabSelect={onTabSelect}
                            data-testid='view-edit-case-tab-list'
                        >
                            {visibleCaseTabOptions.map((tab) => (
                                <OverflowItem
                                    key={tab.value}
                                    id={tab.value}
                                    priority={tab.value === selectedTab ? 1 : 0}
                                >
                                    <Tab
                                        className={tabClasses}
                                        value={tab.value}
                                        data-testid={`view-edit-case-tab-${tab.value}`}
                                    >
                                        {tab.label}
                                    </Tab>
                                </OverflowItem>
                            ))}
                            <TabOverflowMenu
                                className={styles.overflowMenuButton}
                                tabs={visibleCaseTabOptions}
                                onTabChange={onTabChange}
                            />
                        </TabList>
                    </Overflow>
                    <Column flex='1' overflowY='hidden'>
                        <CaseServicesProvider
                            value={{
                                caseForm,
                                caseService,
                                taskService,
                                documentService,
                                eventService,
                                noteService,
                                expenseService,
                                timeTrackingService,
                                caseEngagementService,
                                invoiceService,
                                milestoneService,
                            }}
                        >
                            {selectedTab === CaseTabs.caseInfo && (
                                <>
                                    <CaseInfoPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.engagement && canViewCaseEngagement() && (
                                <>
                                    <EngagementPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.milestones && canViewMilestones() && (
                                <>
                                    <CaseMilestonesPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.caseTasks && canViewTasks() && (
                                <>
                                    <CaseTasksPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.caseEvents && canViewEvents() && (
                                <>
                                    <CaseEventsPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.caseNotes && canViewNotes() && (
                                <>
                                    <CaseNotesPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.caseDocuments && canViewDocuments() && (
                                <>
                                    <CaseDocumentsPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.caseTimeTracking && canViewTimeTracking() && (
                                <>
                                    <TimeTrackingPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.invoices && canViewInvoices() && (
                                <>
                                    <CaseInvoicesPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.insights && canViewInsights() && (
                                <>
                                    <CaseInsightsPage />
                                </>
                            )}
                            {selectedTab === CaseTabs.clientPortal && canViewClientPortal() && (
                                <>
                                    <CaseClientPortalPage />
                                </>
                            )}
                        </CaseServicesProvider>
                    </Column>
                </Column>
            </Column>
            <ConfirmationDialog
                title='Confirm Delete Case'
                icon={<DeleteRegular />}
                text={`Are you sure you want to delete this case?`}
                onConfirm={onDeleteCaseConfirm}
                open={delConfirmOpen}
                onOpenChange={onDeleteCaseConfirmOpenChange}
            />
            <ConfirmationDialog
                title='Apply Template?'
                icon={<DeleteRegular />}
                text={
                    <Column>
                        <Text
                            size={400}
                        >{`Are you sure you want to apply the "${templateToApply?.templateName}" template to this existing case?`}</Text>
                        <Column gap='small'>
                            <Text size={300}>
                                <Text weight='semibold'>CAUTION:</Text> Applying this template will change case information
                                as follows:
                                <ul>
                                    <li>
                                        <Text weight='semibold'>
                                            Case Type, {caseForm.values.shortDescription?.fieldName}, Overview, and Allowed
                                            Statuses
                                        </Text>{' '}
                                        may be overriden.
                                    </li>
                                    <li>
                                        Your <Text weight='semibold'>Engagement Document</Text> may be altered to match this
                                        case template.
                                    </li>
                                    <li>
                                        <Text weight='semibold'>Milestones</Text> from the template will{' '}
                                        <Text weight='semibold'>ONLY</Text> be applied if there are no milestones presently
                                        in this case.
                                    </li>
                                    <li>
                                        In addition, some tasks and events may be added to your existing lists. Please check
                                        for duplicates after this action.
                                    </li>
                                </ul>
                            </Text>
                        </Column>
                    </Column>
                }
                onConfirm={onApplyTemplateConfirm}
                open={applyTemplateConfirmOpen}
                onOpenChange={onApplyTemplateConfirmOpenChange}
            />
            <AuditHistoryDialog
                caseId={caseid}
                open={auditHistoryOpen}
                onOpenChange={(isOpen: boolean) => setAuditHistoryOpen(isOpen)}
            />
        </>
    );
};

type TabOverflowMenuItemProps = {
    tab: CaseTabOption;
    onTabChange: (tab: string) => void;
};
const TabOverflowMenuItem = (props: TabOverflowMenuItemProps) => {
    const { tab, onTabChange } = props;
    const isVisible = useIsOverflowItemVisible(tab.value);

    if (isVisible) {
        return null;
    }

    return (
        <MenuItem
            onClick={() => onTabChange(tab.value)}
            data-testid={`view-edit-case-tab-overflow-item-${tab.value}`}
        >
            {tab.label}
        </MenuItem>
    );
};

type TabOverflowMenuProps = {
    tabs: CaseTabOption[];
    onTabChange: (tab: string) => void;
    className?: string;
};
const TabOverflowMenu = (props: TabOverflowMenuProps) => {
    const { tabs, onTabChange, className } = props;
    const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();

    if (!isOverflowing) {
        return null;
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip content={`${overflowCount} more`} relationship='label'>
                    <Button
                        ref={ref}
                        className={className}
                        appearance='subtle'
                        icon={<MoreHorizontalRegular />}
                        aria-label={`${overflowCount} more tabs`}
                        data-testid='view-edit-case-tab-overflow-button'
                    />
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {tabs.map((tab) => (
                        <TabOverflowMenuItem key={tab.value} tab={tab} onTabChange={onTabChange} />
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};
